"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/feedback";
import {
  addMarkedReadIds,
  applyMarkAsReadToNotificationsCache,
  applyMarkAsReadToUnreadCache,
  getMarkedReadQueryKey,
} from "@/features/notifications/utils/notification-read-state";
import { useAccessToken } from "@/hooks/shared/useAccessToken";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import {
  type GetNotificationResponse,
  type GetUnreadNotificationCountResponse,
  NotificationsApi,
  type PaginatedResultOfGetNotificationResponse,
} from "@/openapi/generated";

interface UseNotificationsMarkAsReadProps {
  userId?: number;
  onSuccess?: () => void;
}

type UnreadQuerySnapshot = Array<
  [
    queryKey: readonly unknown[],
    data: PaginatedResultOfGetNotificationResponse | undefined,
  ]
>;
type UnreadCountQuerySnapshot = Array<
  [
    queryKey: readonly unknown[],
    data: GetUnreadNotificationCountResponse | undefined,
  ]
>;

export { applyMarkAsReadToUnreadCache } from "@/features/notifications/utils/notification-read-state";

export const useNotificationsMarkAsRead = ({
  userId,
  onSuccess,
}: UseNotificationsMarkAsReadProps = {}) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const toast = useToast();

  const mutation = useMutation({
    mutationFn: async (notificationIds: string[]) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(NotificationsApi, accessToken);
      return service.apiNotificationsPut({
        requestBody: notificationIds,
      });
    },
    onMutate: async (notificationIds) => {
      await queryClient.cancelQueries({
        queryKey: [QueryKeys.NotificationsUnread],
      });
      await queryClient.cancelQueries({
        queryKey: [QueryKeys.NotificationsUnreadCount],
      });

      const previousUnread =
        queryClient.getQueriesData<PaginatedResultOfGetNotificationResponse>({
          queryKey: [QueryKeys.NotificationsUnread],
        }) as UnreadQuerySnapshot;

      const previousUnreadCount =
        queryClient.getQueriesData<GetUnreadNotificationCountResponse>({
          queryKey: [QueryKeys.NotificationsUnreadCount],
        }) as UnreadCountQuerySnapshot;

      queryClient.setQueriesData<PaginatedResultOfGetNotificationResponse>(
        { queryKey: [QueryKeys.NotificationsUnread] },
        (old) => applyMarkAsReadToUnreadCache(old, notificationIds),
      );

      queryClient.setQueriesData<GetUnreadNotificationCountResponse>(
        { queryKey: [QueryKeys.NotificationsUnreadCount] },
        (old) =>
          old == null
            ? old
            : {
                count: Math.max(0, Number(old.count) - notificationIds.length),
              },
      );

      queryClient.setQueriesData(
        { queryKey: [QueryKeys.Notifications] },
        (
          old:
            | PaginatedResultOfGetNotificationResponse
            | GetNotificationResponse[]
            | undefined,
        ) => applyMarkAsReadToNotificationsCache(old, notificationIds),
      );

      if (userId) {
        addMarkedReadIds(queryClient, userId, notificationIds);
      }

      return { previousUnread, previousUnreadCount };
    },
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.NotificationsUnread, userId],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.NotificationsUnreadCount, userId],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.Notifications, userId],
        });
      }

      onSuccess?.();
    },
    onError: (error: unknown, notificationIds, context) => {
      context?.previousUnread?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      context?.previousUnreadCount?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });

      if (userId && notificationIds.length > 0) {
        queryClient.setQueryData<string[]>(
          getMarkedReadQueryKey(userId),
          (old = []) =>
            old.filter(
              (id) =>
                !notificationIds
                  .map((notificationId) => String(notificationId).toLowerCase())
                  .includes(String(id).toLowerCase()),
            ),
        );
      }

      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to mark notifications as read";

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
      });
    },
  });

  return mutation;
};
